import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { useAdmin } from './AdminHooks';
import { cn } from '../../lib/utils';

const ModernCodesTab = () => {
  const { codes, fetchCodes, createCode, updateCode, deleteCode, user } = useAdmin();
  const [createOpen, setCreateOpen] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newPercent, setNewPercent] = useState('');
  const [newFlat, setNewFlat] = useState('');
  const [newActive, setNewActive] = useState(true);
  const [editingId, setEditingId] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editCode, setEditCode] = useState('');
  const [editPercent, setEditPercent] = useState('');
  const [editFlat, setEditFlat] = useState('');
  const [editActive, setEditActive] = useState(true);

  useEffect(() => { fetchCodes(); }, []);

  const formatEUR = (n) => new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(n || 0);

  const openEdit = (c) => {
    setEditingId(c.id);
    setEditCode(c.code || '');
    setEditPercent(c.percent != null ? String(c.percent) : '');
    setEditFlat(c.flat != null ? String(c.flat) : '');
    setEditActive(!!c.active);
    setEditOpen(true);
  };

  if (user && user.role !== 'owner' && !((user.allowedTabs || []).includes('codes'))) {
    return (
      <Card className="bg-slate-900/40 border-slate-700">
        <CardContent className="p-6">
          <p className="text-slate-300">Accès refusé.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Codes de réduction</h2>
          <p className="text-slate-400">{codes.length} code(s)</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setCreateOpen(true)}
            className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Plus size={14} className="mr-2" />
            Nouveau code
          </Button>
        </div>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Codes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-300">Code</TableHead>
                  <TableHead className="text-center border-l text-slate-300 border-slate-700">%</TableHead>
                  <TableHead className="text-center border-l text-slate-300 border-slate-700">Réduction</TableHead>
                  <TableHead className="text-center border-l text-slate-300 border-slate-700">Actif</TableHead>
                  <TableHead className="text-right border-l text-slate-300 border-slate-700">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-slate-400">Aucun code</TableCell>
                  </TableRow>
                ) : codes.map(c => (
                  <TableRow key={c.id} className="border-slate-700 hover:bg-slate-800/30">
                    <TableCell className="text-white">{c.code}</TableCell>
                    <TableCell className="text-center">{c.percent != null ? <Badge className="bg-slate-700 text-slate-200 border-slate-600">{c.percent}%</Badge> : '—'}</TableCell>
                    <TableCell className="text-center">{c.flat != null ? <Badge className="bg-slate-700 text-slate-200 border-slate-600">{formatEUR(c.flat)}</Badge> : '—'}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={cn(c.active ? 'text-green-300 bg-green-500/20 border-green-500/30' : 'text-slate-300 bg-slate-700 border-slate-600')}>
                        {c.active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEdit(c)}
                          className="text-slate-300 hover:text-white hover:bg-slate-700"
                          title="Modifier"
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { if (window.confirm('Supprimer ce code ?')) deleteCode(c.id); }}
                          className="text-slate-300 hover:text-red-400 hover:bg-red-500/10"
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Nouveau code</DialogTitle>
            <DialogDescription className="text-slate-300">Créer un code avec % et/ou réduction fixe</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <Input
              placeholder="CODE"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value.toUpperCase())}
              className="text-white bg-slate-900 border-slate-700 placeholder-slate-400 sm:col-span-2"
            />
            <Input
              placeholder="%"
              type="number"
              value={newPercent}
              onChange={(e) => setNewPercent(e.target.value)}
              className="text-white bg-slate-900 border-slate-700 placeholder-slate-400"
            />
            <Input
              placeholder="Montant"
              type="number"
              value={newFlat}
              onChange={(e) => setNewFlat(e.target.value)}
              className="text-white bg-slate-900 border-slate-700 placeholder-slate-400"
            />
            <label className="flex gap-2 items-center text-slate-300 sm:col-span-4">
              <Checkbox checked={newActive} onCheckedChange={(v) => setNewActive(!!v)} />
              Actif
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => {
                const payload = {
                  code: newCode.trim().toUpperCase(),
                  percent: newPercent ? parseFloat(newPercent) : undefined,
                  flat: newFlat ? parseFloat(newFlat) : undefined,
                  active: newActive,
                };
                createCode(payload);
                setCreateOpen(false);
                setNewCode(''); setNewPercent(''); setNewFlat(''); setNewActive(true);
              }}
              className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              Créer
            </Button>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white">
              Annuler
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Modifier le code</DialogTitle>
            <DialogDescription className="text-slate-300">Mettre à jour les valeurs</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <Input
              placeholder="CODE"
              value={editCode}
              onChange={(e) => setEditCode(e.target.value.toUpperCase())}
              className="text-white bg-slate-900 border-slate-700 placeholder-slate-400 sm:col-span-2"
            />
            <Input
              placeholder="%"
              type="number"
              value={editPercent}
              onChange={(e) => setEditPercent(e.target.value)}
              className="text-white bg-slate-900 border-slate-700 placeholder-slate-400"
            />
            <Input
              placeholder="Montant"
              type="number"
              value={editFlat}
              onChange={(e) => setEditFlat(e.target.value)}
              className="text-white bg-slate-900 border-slate-700 placeholder-slate-400"
            />
            <label className="flex gap-2 items-center text-slate-300 sm:col-span-4">
              <Checkbox checked={editActive} onCheckedChange={(v) => setEditActive(!!v)} />
              Actif
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => {
                if (!editingId) return;
                const fields = {
                  code: (editCode || '').trim().toUpperCase(),
                  percent: editPercent ? parseFloat(editPercent) : null,
                  flat: editFlat ? parseFloat(editFlat) : null,
                  active: editActive,
                };
                updateCode(editingId, fields);
                setEditOpen(false);
                setEditingId('');
              }}
              className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              Enregistrer
            </Button>
            <Button variant="outline" onClick={() => { setEditOpen(false); setEditingId(''); }} className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white">
              Annuler
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModernCodesTab;
